import { BasketItemFlags } from '@virginexperiencedays/basket/src/utils/basketItemFlags';
import { useForceRefreshOnPageShow, useScrollLock } from '@virginexperiencedays/hooks';
import { slugToName } from '@virginexperiencedays/search/utils/category';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { formatTrackingData } from '../../../../libs/tracking/pdp';
import { isB2bProduct } from '../../../../utils/b2b/isB2bProduct';
import { useValidB2bRoute } from '../../../../utils/b2b/useValidB2bRoute';
import { breadcrumbs } from '../../../SEO/StructuredData/breadcrumbs';
import { useHandleUpdateBasketCount } from '../../hooks/useHandleUpdateBasketCount';
import type { PageProps, PageUIProps, PageUIWrapperProps } from '../Page';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import { PageUI } from './PageUI';
import { PageUIWrapper } from './PageUIWrapper';
import { reviewsExtractor } from './extractors';

export const Page = ({
  collectionSlug,
  isActive,
  product,
  productPromo,
  reviews: individualReviews,
  slugBreadcrumbNameMap,
  slugNameMap,
  trackingPageType,
}: PageProps) => {
  const { categoryHierarchy, isCollectionProduct, isCollectionChoice, promocode, sku, slug } =
    product;

  const router = useRouter();

  // category and category hierarchy
  const { lvl0, lvl1, lvl2 } = categoryHierarchy ?? {};
  const getSlugName = (slug: string) => slugNameMap?.[slug] ?? slugToName(slug);

  const legacyTitleCategory: { categoryName: string; href: string } = {
    categoryName: lvl0 && getSlugName(lvl0),
    href: `${router.basePath}/${lvl0}`, // revisit and make sure it should be parent category(lvl0) or lvl2 ?? lvl1 ?? lvl0
  };

  // B2B Sales Reporting
  const { visitedValidRoutes } = useValidB2bRoute();
  const isB2b = isB2bProduct(product) && visitedValidRoutes();
  const basketItemFlags: BasketItemFlags[] = isB2b ? [BasketItemFlags.REPORT_AS_B2B] : [];

  // hooks
  const { quickBasketOpen, handleQuickBasketOpen, setQuickBasketOpen } = useQuickBasket();
  const handleUpdateBasketCount = useHandleUpdateBasketCount();
  const { unlockScroll } = useScrollLock([quickBasketOpen]);
  useForceRefreshOnPageShow(unlockScroll);

  // recently viewed
  useRecentlyViewed({ ...product, productPromo });

  // TODO LP-6697 I don't think we want this to be our fallback
  // discuss with UI/UX a better alternative if required
  if (router.isFallback) return <div>Loading ...</div>;

  const collectionUrl = `/collection/${collectionSlug}`;
  const isCollectionPages = isCollectionProduct || isCollectionChoice;
  const seoPathOverride = isCollectionChoice
    ? `${collectionUrl}/product/${slug}`
    : `/collection/${slug}`;

  // Apply rating logic to decide whether to render Star Rating and Customer Reviews or not
  const filteredReviews = reviewsExtractor(product, {
    individualReviews,
  });

  const trackingData = formatTrackingData({ product, trackingPageType, productPromo });

  const wrapperProps: Omit<PageUIWrapperProps, 'children'> = {
    breadcrumbs,
    handleQuickBasketOpen,
    handleUpdateBasketCount,
    isCollectionChoice,
    isCollectionPages,
    isCollectionProduct,
    lvl0,
    lvl1,
    lvl2,
    product,
    promocode,
    quickBasketOpen,
    reviewsList: filteredReviews?.reviews,
    seoPathOverride,
    setQuickBasketOpen,
    sku,
    slugBreadcrumbNameMap,
  };

  const layoutProps: PageUIProps = {
    flags: basketItemFlags,
    handleQuickBasket: handleQuickBasketOpen,
    isActive,
    product,
    productPromo,
    reviews: filteredReviews,
    isCollectionProduct,
    slugNameMap,
    trackingData,
  };

  return (
    /**
     * The entire PageUI component is reused by React when moving across PDPs,
     * causing some state to persist (e.g. View All Photos modal staying open,
     * previous product data flashing).
     *
     * To force React to re-render a new component per page, the whole component is
     * keyed with router.asPath using a wrapper.
     */
    <PageUIWrapper {...wrapperProps} key={router.asPath}>
      <PageUI {...layoutProps} />
    </PageUIWrapper>
  );
};

function useQuickBasket() {
  const [quickBasketOpen, setQuickBasketOpen] = useState(false);

  const handleQuickBasketOpen = (isOpen: boolean) => {
    setQuickBasketOpen(isOpen);
  };

  return { quickBasketOpen, handleQuickBasketOpen, setQuickBasketOpen } as const;
}
